import React, { useState, useCallback, useMemo, useRef, createContext, useContext, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import { usePracticeSync } from "../hooks/usePracticeSync";
import { practiceDataUtil } from '../helpers/practiceDataUtil';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PracticeDataProvider } from '../contexts/PracticeDataContext';
import PracticeSettings from '../components/PracticeSettings';
import RequireAuth from '../components/RequireAuth';
import { Button } from "../components/ui/button"; 
import { practiceRateCache } from '../utils/practiceRateCache';

import { PracticeSelector } from "../components/invoice/PracticeSelector";
import MonthlyInvoiceForm from "../components/invoice/MonthlyInvoiceForm";
import { InvoicePreview } from "../components/invoice/InvoicePreview";
import { InvoiceActions } from "../components/invoice/InvoiceActions"; 
import { InvoiceSummary } from "../components/invoice/InvoiceSummary";
import DailyRecordLog from "../components/DailyRecordLog"; // Import the DailyRecordLog component

import { format } from 'date-fns';
import { decryptData } from '../encryptionUtil.js';
import { getNextInvoiceNumber, saveInvoice } from '../invoiceUtils.js';
import { generateInvoicePDF } from '../InvoicePDFGenerator';
import { invoiceNumberService } from '../services/invoiceNumberService.js'; // Add this import to fix the errors
import { SimpleCalendar } from "../components/ui/simple-calendar"; // Keep this import
import HelpIcon from '../components/ui/HelpIcon'; // Import HelpIcon
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { ROUTES } from '../App'; // Import ROUTES for navigation
import { PlusIcon } from 'lucide-react'; // Add PlusIcon import
import { useTheme } from '../components/ui/theme-provider'; // Import useTheme
import { cn } from "../lib/utils"; // Import cn
import { useInvoiceDraft } from '../hooks/useInvoiceDraft';

export const InvoiceContext = createContext(null);

export const useInvoice = () => {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error('useInvoice must be used within an InvoiceProvider');
  }
  return context;
};

const InvoiceApp = ({ user, initialSelectedPractices = [], subscriptionStatus }) => {
  const renderCounts = useRef({
    main: 0,
    practiceSelector: 0,
    invoiceForm: 0,
    preview: 0,
    summary: 0,
    actions: 0
  }).current;

  const isInitialRender = useRef(true);
  const prevSelectedPracticesRef = useRef([]);
  const pendingRestoreEntriesRef = useRef(null);
  
  const { toast } = useToast();
  const auth = getAuth();
  const navigate = useNavigate(); // Hook for navigation
  const { theme } = useTheme(); // Get theme
  
  const { getPractice, initialized: practicesInitialized, getAndCachePractice } = usePracticeSync({ autoInitialize: true });
  
  const safeIncrementRenderCount = useCallback((component) => {
    if (renderCounts[component] !== undefined) {
      renderCounts[component] = (renderCounts[component] || 0) + 1;
    }
  }, [renderCounts]);
  
  renderCounts.main += 1;

  const [selectedPractices, setSelectedPractices] = useState(initialSelectedPractices || []);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [practiceDetails, setPracticeDetails] = useState(null);
  const [showPracticeSettings, setShowPracticeSettings] = useState(false);
  
  const [activeTab, setActiveTab] = useState('monthly'); // State for active tab
  const [showSettings, setShowSettings] = useState(false);
  
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    name: '',
    businessName: '', // Added businessName
    address1: '',
    address2: '',
    address3: '', // Added address3
    address4: '', // Added address4
    city: '',
    postcode: '',
    email: '',
    phone: '',
    bankName: '',
    sortCode: '',
    accountNumber: '',
    notes: '', // Keep notes field
    uid: user?.uid || null
  });
  
  const [rateType, setRateType] = useState('percentage');
  const [rateValue, setRateValue] = useState('');
  const [entries, setEntries] = useState([
    { date: null, hours: '', patients: [{ count: '', rate: '', description: '' }], isExpanded: true }
  ]);
  const [total, setTotal] = useState(null);
  const [summary, setSummary] = useState([]);
  // New: control whether summary UI is shown
  const [showSummary, setShowSummary] = useState(false);
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [invoiceSaved, setInvoiceSaved] = useState(false);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState(null); // Add this line
  const [previewGenerated, setPreviewGenerated] = useState(false); // Add this line
  
  const [practiceDataVersion, setPracticeDataVersion] = useState(0);
  const [practiceDataTimestamp, setPracticeDataTimestamp] = useState(practiceDataUtil.getLastUpdateTimestamp());
  
  const pdfExportComponent = useRef(null);
  const [calendarOpen, setCalendarOpen] = useState(null); // Add this state for managing calendar open/close
  const [activeView, setActiveView] = useState('monthly'); // Add state for tracking which view is active

  // ── Monthly invoice autosave draft ──────────────────────────────────────
  const monthlyDraftKey = user?.uid ? `invoice-monthly-${user.uid}` : null;
  // practiceDetails is the resolved practice object from the JSON — the authoritative name source
  const monthlyDraftData = JSON.stringify({ entries, rateType, rateValue, selectedPractice, selectedPracticeName: practiceDetails?.prac_name || null });
  // Only autosave when the form has meaningful data — at least one entry with a
  // date or hours filled in.  This is the primary guard against phantom drafts:
  // a blank/reset form (after "New Invoice" or on initial load) can never
  // autosave regardless of how many re-renders occur.
  const monthlyDraftHasContent = useCallback(
    (d) => Array.isArray(d?.entries) && d.entries.some(e => e.date || e.hours !== ''),
    []
  );
  const { savedDraft: monthlyDraft, clearDraft: clearMonthlyDraft, dismissDraft: dismissMonthlyDraft, acknowledgeDraft: acknowledgeMonthlyDraft, pauseSave: pauseMonthlyDraft } =
    useInvoiceDraft(monthlyDraftKey, JSON.parse(monthlyDraftData), 2000, monthlyDraftHasContent);

  const handleRestoreMonthlyDraft = useCallback(() => {
    if (!monthlyDraft) return;
    if (monthlyDraft.entries) setEntries(monthlyDraft.entries);
    if (monthlyDraft.rateType) setRateType(monthlyDraft.rateType);
    if (monthlyDraft.rateValue !== undefined) setRateValue(monthlyDraft.rateValue);
    if (monthlyDraft.selectedPractice) {
      // Pre-seed the cache with the draft's own rates so the rate-load effect
      // (which fires when selectedPractice changes) re-applies these values
      // rather than overwriting with a stale cached entry.
      if (user?.uid && monthlyDraft.rateType && monthlyDraft.rateValue !== undefined) {
        practiceRateCache.set(
          monthlyDraft.selectedPractice, user.uid,
          monthlyDraft.rateType, monthlyDraft.rateValue
        );
      }
      // Safety net: store entries in ref so the effect below can re-apply them
      // after all selectedPractice side-effects (rate-cache, Firestore) have settled.
      if (monthlyDraft.entries) {
        pendingRestoreEntriesRef.current = monthlyDraft.entries;
      }
      setSelectedPractice(monthlyDraft.selectedPractice);
      // Also resolve practiceDetails so the save guard (!practiceDetails) doesn't
      // block saving a restored draft. Try sources in order of reliability:
      // 1. selectedPractices (full objects already in memory)
      // 2. practiceDataUtil cache (synchronous, populated at app start)
      // 3. getAndCachePractice (async fallback)
      const fromSelected = selectedPractices.find(p =>
        typeof p === 'object' && p.prac_code === monthlyDraft.selectedPractice && p.prac_name
      );
      const fromCache = !fromSelected
        ? practiceDataUtil.practiceCache.allPractices?.find(
            p => p.prac_code === monthlyDraft.selectedPractice
          )
        : null;
      if (fromSelected) {
        setPracticeDetails(fromSelected);
      } else if (fromCache) {
        setPracticeDetails(fromCache);
      } else {
        const result = getAndCachePractice(monthlyDraft.selectedPractice);
        if (result && typeof result.then === 'function') {
          result.then(fetched => { if (fetched) setPracticeDetails(fetched); }).catch(() => {});
        }
      }
    }
    acknowledgeMonthlyDraft();
  }, [monthlyDraft, acknowledgeMonthlyDraft, user, selectedPractices, setPracticeDetails, getAndCachePractice]);
  // ────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    prevSelectedPracticesRef.current = selectedPractices;
  }, [selectedPractices]);

  // Load cached rate when practice changes
  useEffect(() => {
    if (selectedPractice && user?.uid) {
      const cached = practiceRateCache.get(selectedPractice, user.uid);
      if (cached) {
        console.log(`📦 Loading cached rate for practice ${selectedPractice}:`, cached);
        setRateType(cached.rateType);
        setRateValue(cached.rateValue);
      } else {
        // Reset to defaults if no cache
        console.log(`📦 No cached rate for practice ${selectedPractice}, using defaults`);
        // Don't reset - keep current values unless explicitly cleared
        // This prevents wiping values when first selecting a practice
      }
    }
  }, [selectedPractice, user?.uid]);

  // Save rate to cache when it changes (debounced via rateValue check)
  useEffect(() => {
    if (selectedPractice && user?.uid && rateValue && rateValue.trim() !== '') {
      // Only cache if there's a meaningful value
      practiceRateCache.set(selectedPractice, user.uid, rateType, rateValue);
      console.log(`💾 Cached rate for practice ${selectedPractice}:`, { rateType, rateValue });
    }
  }, [selectedPractice, user?.uid, rateType, rateValue]);

  // Safety net: re-apply draft entries after all selectedPractice side-effects settle.
  // Runs after the rate-cache effects above. If any effect in the chain cleared entries,
  // this guarantees the restored entries survive and the form shows the correct line items.
  useEffect(() => {
    if (selectedPractice && pendingRestoreEntriesRef.current !== null) {
      setEntries(pendingRestoreEntriesRef.current);
      pendingRestoreEntriesRef.current = null;
    }
  }, [selectedPractice]);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!auth.currentUser?.uid) {
        console.log("No user ID, skipping profile fetch.");
        return;
      }
      //console.log("Fetching profile data for user:", auth.currentUser.uid);

      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (process.env.NODE_ENV === 'development') {
            console.log("Raw user data from Firestore:", userData);
          }

          // Define fields to decrypt (add all potentially encrypted fields)
          const fieldsToDecrypt = [
            'name', 'businessName', 'address1', 'address2', 'address3', 'address4',
            'city', 'postcode', 'email', 'phone',
            'bankName', 'sortCode', 'accountNumber', 'notes'
          ];

          let loadedProfileData = { uid: auth.currentUser.uid };

          // Helper function to check if a string looks encrypted
          const isEncryptedString = (str) => typeof str === 'string' && str.startsWith('U2FsdGVkX1');

          // Process each field, attempting decryption if necessary
          for (const key of fieldsToDecrypt) {
            const value = userData[key];
            if (isEncryptedString(value)) {
              try {
                loadedProfileData[key] = await decryptData(value);
                //console.log(`Decrypted ${key}`);
              } catch (error) {
                console.error(`❌ Failed to decrypt ${key}:`, error);
                loadedProfileData[key] = ''; // Set to empty string on decryption failure
              }            } else {
              // Use the value directly if it's not encrypted or not a string
              loadedProfileData[key] = value || ''; // Use empty string if null/undefined
              if (process.env.NODE_ENV === 'development') {
                //console.log(`Used direct value for ${key}`);
              }
            }
          }

          // Handle legacy 'address' and 'bankDetails' fields if they exist and new fields are empty
          if (!loadedProfileData.address1 && isEncryptedString(userData.address)) {
            try {
              const decryptedAddress = await decryptData(userData.address);
              // Attempt to split legacy address - this is a best guess
              const addressLines = decryptedAddress.split('\n');
              loadedProfileData.address1 = addressLines[0] || '';
              loadedProfileData.address2 = addressLines[1] || '';
              // Assume city/postcode might be on the last line
              const lastLineParts = (addressLines[addressLines.length - 1] || '').split(',');
              loadedProfileData.city = lastLineParts[0]?.trim() || '';
              loadedProfileData.postcode = lastLineParts[1]?.trim() || '';
              console.log("Processed legacy 'address' field.");
            } catch (e) { console.error("Failed to process legacy address", e); }
          }
          if (!loadedProfileData.bankName && isEncryptedString(userData.bankDetails)) {
             try {
               const decryptedBank = await decryptData(userData.bankDetails);
               // Attempt to parse legacy bank details - very basic guess
               const bankLines = decryptedBank.split('\n');
               loadedProfileData.bankName = bankLines[0]?.replace('Bank Name:', '').trim() || '';
               loadedProfileData.accountNumber = bankLines[1]?.replace('Account Number:', '').trim() || '';
               loadedProfileData.sortCode = bankLines[2]?.replace('Sort Code:', '').trim() || '';
               console.log("Processed legacy 'bankDetails' field.");
             } catch (e) { console.error("Failed to process legacy bank details", e); }          }


          if (process.env.NODE_ENV === 'development') {
            console.log("Processed profile data:", loadedProfileData);
          }
          setProfileData(prev => ({ ...prev, ...loadedProfileData }));

        } else {
          console.warn('User document does not exist, initializing profile.');
          // Initialize with default structure if no document exists
          setProfileData({
            uid: auth.currentUser.uid,
            name: auth.currentUser.displayName || '',
            email: auth.currentUser.email || '',
            businessName: '', address1: '', address2: '', address3: '', address4: '',
            city: '', postcode: '', phone: '',
            bankName: '', sortCode: '', accountNumber: '', notes: ''
          });
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
        toast({
          title: "Error",
          description: "Failed to load profile data: " + error.message,
          variant: "destructive",
        });
      }
    };

    fetchProfileData();
  }, [auth.currentUser?.uid, toast]); // Dependency on user ID

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  }, []);

  const generateInvoiceNumber = useCallback(async (forSaving = false) => {
    try {
      if (!profileData || !profileData.name) {
        throw new Error('Profile data not available');
      }
      
      // Call the existing service - use forSaving flag to determine if we should persist
      const options = { 
        persistToDatabase: forSaving, 
        forSaving: forSaving 
      };
      
      const formattedNumber = await invoiceNumberService.generateInvoiceNumber(
        user.uid, 
        profileData, 
        options
      );
      
      setCurrentInvoiceNumber(formattedNumber);
      return formattedNumber;
    } catch (error) {
      console.error('Error generating invoice number:', error);
      // Use the proper initial-based fallback format
      const fallbackInitials = profileData?.name ? 
        `${profileData.name[0] || 'X'}${(profileData.name.split(' ')[1] || '')[0] || 'Y'}` : 
        'XY';
      const timestamp = new Date().getTime().toString().slice(-4);
      const fallbackNumber = `${fallbackInitials}${timestamp}`;
      return fallbackNumber;
    }
  }, [user?.uid, profileData]);

  const handleGeneratePreview = async () => {
    if (!profileData) {
      toast({
        title: "Error",
        description: "Please complete your profile before generating an invoice",
        variant: "destructive",
      });
      return;
    }
  
    if (!selectedPractice || !practiceDetails) {
      toast({
        title: "Error",
        description: "Please select a valid practice before generating an invoice",
        variant: "destructive",
      });
      return;
    }
    
    setPreviewLoading(true);
    setPreviewGenerated(false); // Reset when generating new preview
  
    try {
      // Calculate directly and use the result immediately - not relying on state update
      
      // Use a local copy of entries and calculate everything locally
      let totalHours = 0;
      let totalPatients = 0;
      let totalAmount = 0;
      let calculatedSummary = [];
      
      if (entries && entries.length > 0 && rateValue) {
        // Filter entries that have dates and group by date
        const validEntries = entries.filter(entry => entry.date);
        const groupedEntries = validEntries.reduce((acc, entry) => {
          const dateKey = format(entry.date, "yyyy-MM-dd");
          if (!acc[dateKey]) {
            acc[dateKey] = [];
          }
          acc[dateKey].push(entry);
          return acc;
        }, {});
  
        // Process each date group - same logic as calculateTotal
        Object.entries(groupedEntries).forEach(([dateKey, dateEntries]) => {
          const formattedDate = format(dateEntries[0].date, "dd MMM yyyy");
          let dateTotal = 0;
          let datePatients = 0;
          let dateHours = 0;
          let dateLines = [];
  
          dateEntries.forEach(entry => {
            if (rateType === 'hourly') {
              const hours = parseFloat(entry.hours) || 0;
              const hourlyRate = parseFloat(rateValue) || 0;
              const entryAmount = hours * hourlyRate;
              dateHours += hours;
              dateTotal += entryAmount;
              // Include description if present
              const descText = entry.description ? ` (${entry.description})` : '';
              dateLines.push(`${hours} hours × £${hourlyRate.toFixed(2)}${descText}`);
            }
            else if (rateType === 'percentage') {
              if (entry.patients && entry.patients.length > 0) {
                entry.patients.forEach(patient => {
                  const count = parseInt(patient.count) || 0;
                  const rate = parseFloat(patient.rate) || 0;
                  const patientCollections = count * rate; // Collections without percentage
                  
                  datePatients += count;
                  dateTotal += patientCollections; // Accumulate collections (no percentage yet)
                  // Show patient details WITHOUT percentage
                  const pluralText = count !== 1 ? 's' : '';
                  dateLines.push(`${count} patient${pluralText} @ £${rate.toFixed(2)}${patient.description ? ` (${patient.description})` : ''}`);
                });
              }
            }
            else if (rateType === 'per_patient') {
              if (entry.patients && entry.patients.length > 0) {
                entry.patients.forEach(patient => {
                  const count = parseInt(patient.count) || 0;
                  const rate = parseFloat(patient.rate) || 0;
                  const patientAmount = count * rate;
                  
                  datePatients += count;
                  dateTotal += patientAmount;
                  // Format: X × £Y.YY (description)
                  const descText = patient.description ? 
                    `${count} × £${rate.toFixed(2)} (${patient.description})` : 
                    `${count} × £${rate.toFixed(2)}`;
                  dateLines.push(descText);
                });
              }
            }
            else if (rateType === 'fixed') {
              const fixedAmount = parseFloat(rateValue) || 0;
              dateTotal += fixedAmount;
              dateHours += parseFloat(entry.hours) || 0;
              // Include description if present
              const descText = entry.description ? ` (${entry.description})` : '';
              dateLines.push(`Fixed rate: £${fixedAmount.toFixed(2)}${descText}`);
            }
          });
  
          // Add single summary entry for the date with all details
          calculatedSummary.push({
            label: formattedDate,
            value: `${dateLines.join(' | ')} = £${dateTotal.toFixed(2)}`
          });
  
          // Update totals
          totalHours += dateHours;
          totalPatients += datePatients;
          totalAmount += dateTotal;
        });
        
        // For percentage invoices, add special summary rows
        if (rateType === 'percentage') {
          const percentageRate = parseFloat(rateValue) || 0;
          const subtotal = totalAmount; // totalAmount is now collections (without percentage)
          const percentageAmount = subtotal * (percentageRate / 100);
          const invoiceTotal = percentageAmount;
          
          // Add separator and special rows
          calculatedSummary.push({
            label: 'Total Charged to Patients',
            value: `£${subtotal.toFixed(2)}`,
            isSubtotal: true
          });
          calculatedSummary.push({
            label: `Commission (${percentageRate}%)`,
            value: `£${percentageAmount.toFixed(2)}`,
            isPercentage: true
          });
          calculatedSummary.push({
            label: 'Invoice Total',
            value: `£${invoiceTotal.toFixed(2)}`,
            isTotal: true
          });
          
          // Update totalAmount to be the actual invoice total
          totalAmount = invoiceTotal;
        }
        
        // Update state for later use - but don't rely on it for immediate use
        setTotal({
          hours: totalHours,
          patients: totalPatients,
          amount: totalAmount
        });
        setSummary(calculatedSummary);
      } else {
        // If no entries, use existing summary data
        calculatedSummary = summary;
        totalAmount = total?.amount || 0;
      }
      
      let invoiceNum;
      try {
        // For preview, use the getPreviewNumber which doesn't increment
        invoiceNum = await invoiceNumberService.getPreviewNumber(user.uid, profileData);
        setPreviewInvoiceNumber(invoiceNum);
      } catch (error) {
        // Fallback code for invoice number...
        invoiceNum = 'PREVIEW';
      }
  
      // Format the invoice date
      const invoiceDate = format(new Date(), "dd MMMM yyyy");

      // --- Updated HTML Generation ---
      const renderAddressLine = (line) => (line && line !== 'NA' ? `${line}<br>` : '');
      const renderOptionalField = (label, value) => (value && value !== 'NA' ? `<p style="margin: 5px 0; color: #333;">${label}: ${value}</p>` : '');

      const profileAddressHtml = `\
        ${renderAddressLine(profileData?.address1)}
        ${renderAddressLine(profileData?.address2)}
        ${renderAddressLine(profileData?.address3)}
        ${renderAddressLine(profileData?.address4)}
        ${profileData?.city || ''}${profileData?.city && profileData?.postcode ? ', ' : ''}${profileData?.postcode || ''}
      `.trim();

      const practiceAddressHtml = `\
        ${renderAddressLine(practiceDetails?.address1)}
        ${renderAddressLine(practiceDetails?.address2)}
        ${renderAddressLine(practiceDetails?.address3)}
        ${renderAddressLine(practiceDetails?.address4)}
        ${practiceDetails?.address5 || ''}${practiceDetails?.address5 && practiceDetails?.postcode ? ', ' : ''}${practiceDetails?.postcode || ''}
      `.trim();


      const htmlContent = `\
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; border: 1px solid #eee;">
          <!-- Centered INVOICE title -->
          <div style="text-align: center; margin-bottom: 32px;">
            <span style="color: #8a2be2; font-size: 2em; font-weight: 500; letter-spacing: 2px;">INVOICE</span>
          </div>
          <!-- Addresses row -->
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px;">
            <div style="max-width: 48%; text-align: left;">
              <div style="color: #222; font-size: 1.1em; margin-bottom: 2px;">${profileData?.name || 'Your Name'}</div>
              ${profileData?.businessName ? `<div style="margin-bottom: 6px; color: #333;">${profileData.businessName}</div>` : ''}
              <div style="margin-bottom: 2px; color: #333;">${profileAddressHtml}</div>
              ${profileData?.email ? `<div style="color: #333;">${profileData.email}</div>` : ''}
              ${profileData?.phone ? `<div style="color: #333;">${profileData.phone}</div>` : ''}
            </div>
            <div style="max-width: 48%; text-align: right;">
              <div style="color: #222; font-size: 1.1em; margin-bottom: 2px;">${practiceDetails?.prac_name || 'Unknown Practice'}</div>
              <div style="margin-bottom: 2px; color: #333;">${practiceAddressHtml}</div>
            </div>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;">
            <div style="color: #222; font-size: 1em; text-align: left;">Invoice Number: ${invoiceNum}</div>
            <div style="color: #222; font-size: 1em; text-align: right;">Date: ${invoiceDate}</div>
          </div>

          <table style="width: 100%; border-collapse: collapse; margin-bottom: 32px;">
            <thead>
              <tr style="background-color: #8a2be2; color: white;">
                <th style="padding: 12px; text-align: left; border-bottom: 1px solid #8a2be2; font-weight: 400;">Date</th>
                <th style="padding: 12px; text-align: left; border-bottom: 1px solid #8a2be2; font-weight: 400;">Description</th>
                <th style="padding: 12px; text-align: right; border-bottom: 1px solid #8a2be2; font-weight: 400;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${calculatedSummary.map(item => {
                // Check if this is a special summary row
                const isSpecialRow = item.isSubtotal || item.isPercentage || item.isTotal;
                
                if (isSpecialRow) {
                  // Special rows: label spans first 2 columns, value in last column
                  const fontWeight = item.isTotal ? 'bold' : 'normal';
                  const borderTop = item.isSubtotal ? '2px solid #8a2be2' : 'none';
                  return '<tr style="border-bottom: 1px solid #e0e0e0;"><td colspan="2" style="padding: 10px; color: #222; vertical-align: top; font-size: 1em; font-weight: ' + fontWeight + '; border-top: ' + borderTop + ';">' + item.label + '</td><td style="padding: 10px; text-align: right; color: #222; vertical-align: top; font-size: 1em; font-weight: ' + fontWeight + '; border-top: ' + borderTop + ';">' + item.value + '</td></tr>';
                } else {
                  // Regular date rows with description = amount format
                  return '<tr style="border-bottom: 1px solid #e0e0e0;"><td style="padding: 10px; color: #222; vertical-align: top; font-size: 1em;">' + item.label + '</td><td style="padding: 10px; color: #222; vertical-align: top; font-size: 1em;">' + item.value.split('=')[0].trim() + '</td><td style="padding: 10px; text-align: right; color: #222; vertical-align: top; font-size: 1em;">£' + (item.value.split('=')[1]?.trim()?.replace('£', '') || '0.00') + '</td></tr>';
                }
              }).join('')}
            </tbody>
          </table>

          <div style="text-align: right; margin-bottom: 48px;">
            <span style="color: #222; font-size: 1.2em; font-weight: bold;">Total: £${totalAmount.toFixed(2)}</span>
          </div>

          <div style="margin-top: 32px;">
            <div style="color: #222; font-size: 1em; font-weight: 400; margin-bottom: 2px;">Bank Details:</div>
            ${profileData?.bankName ? `<div style="color: #222;">Bank: ${profileData.bankName}</div>` : ''}
            ${profileData?.sortCode ? `<div style="color: #222;">Sort Code: ${profileData.sortCode}</div>` : ''}
            ${profileData?.accountNumber ? `<div style="color: #222;">Account Number: ${profileData.accountNumber}</div>` : ''}
            <div style="color: #222; margin-top: 24px;">Payment is due within 30 days from date of invoice</div>
          </div>
        </div>
      `;
      // --- End of Updated HTML Generation ---

      setPreviewHtml(htmlContent);
      setPreviewGenerated(true);
      
      toast({
        title: "Success",
        description: "Invoice preview has been generated",
        variant: "success",
      });
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: "Preview Error",
        description: "Could not generate invoice preview. Please check your profile and entries.",
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!entries || entries.length === 0 || !rateValue) {
      toast({
        title: "Missing Information",
        description: "Please add entries with dates and set a rate value",
        variant: "destructive",
      });
      return;
    }
  
    let totalHours = 0;
    let totalPatients = 0;
    let totalAmount = 0;
    const detailedSummary = [];
  
    // Filter entries that have dates and group by date
    const validEntries = entries.filter(entry => entry.date);
    const groupedEntries = validEntries.reduce((acc, entry) => {
      const dateKey = format(entry.date, "yyyy-MM-dd");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(entry);
      return acc;
    }, {});
  
    // Process each date group
    Object.entries(groupedEntries).forEach(([dateKey, dateEntries]) => {
      const formattedDate = format(dateEntries[0].date, "dd MMM yyyy");
      let dateTotal = 0;
      let datePatients = 0;
      let dateHours = 0;
      let dateLines = [];
  
      dateEntries.forEach(entry => {
        if (rateType === 'hourly') {
          const hours = parseFloat(entry.hours) || 0;
          const hourlyRate = parseFloat(rateValue) || 0;
          const entryAmount = hours * hourlyRate;
          dateHours += hours;
          dateTotal += entryAmount;
          dateLines.push(`${hours} hours × £${hourlyRate.toFixed(2)}`);
        } 
        else if (rateType === 'percentage') {
          console.log('🔥🔥🔥 NEW PERCENTAGE LOGIC IN InvoiceApp.jsx 🔥🔥🔥');
          if (entry.patients && entry.patients.length > 0) {
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const patientCollections = count * rate; // Collections without percentage
              
              datePatients += count;
              dateTotal += patientCollections; // Accumulate collections (no percentage yet)
              
              // Show patient details WITHOUT percentage
              const pluralText = count !== 1 ? 's' : '';
              const line = `${count} patient${pluralText} @ £${rate.toFixed(2)}${patient.description ? ` (${patient.description})` : ''}`;
              console.log('🔥 Building line:', line);
              dateLines.push(line);
            });
          }
        }
        else if (rateType === 'fixed') {
          const fixedAmount = parseFloat(rateValue) || 0;
          dateTotal += fixedAmount;
          dateHours += parseFloat(entry.hours) || 0;
          dateLines.push(`Fixed rate: £${fixedAmount.toFixed(2)}`);
        }
      });
  
      // Add single summary entry for the date with all details
      if (rateType === 'percentage') {
        // For percentage, show collections (no percentage applied yet)
        detailedSummary.push({
          label: formattedDate,
          value: `${dateLines.join(' + ')} = £${dateTotal.toFixed(2)}`
        });
      } else {
        // For other types, keep existing format
        detailedSummary.push({
          label: formattedDate,
          value: `${dateLines.join(' | ')} = £${dateTotal.toFixed(2)}`
        });
      }

      // Update totals
      totalHours += dateHours;
      totalPatients += datePatients;
      totalAmount += dateTotal;
    });
    
    // For percentage invoices, add summary rows and apply percentage
    if (rateType === 'percentage') {
      const percentageRate = parseFloat(rateValue) || 0;
      const totalCollections = totalAmount; // At this point, totalAmount = collections
      const percentageAmount = totalCollections * (percentageRate / 100);
      
      // Add subtotal row
      detailedSummary.push({
        label: 'Subtotal (patient fees)',
        value: `= £${totalCollections.toFixed(2)}`,
        isSubtotal: true
      });
      
      // Add percentage calculation row
      detailedSummary.push({
        label: `Your ${percentageRate}% commission`,
        value: `= £${percentageAmount.toFixed(2)}`,
        isPercentage: true
      });
      
      // Add total row
      detailedSummary.push({
        label: 'Invoice Total',
        value: `= £${percentageAmount.toFixed(2)}`,
        isTotal: true
      });
      
      // Update totalAmount to be the percentage amount
      totalAmount = percentageAmount;
      
      console.log('🔥🔥🔥 PERCENTAGE SUMMARY COMPLETE IN InvoiceApp.jsx:', detailedSummary);
    }
  
    // Update state with calculated values
    setTotal({
      hours: totalHours,
      patients: totalPatients,
      amount: totalAmount
    });
    setSummary(detailedSummary);
  
    toast({
      title: "Total Calculated",
      description: `Total amount: ${formatCurrency(totalAmount)}`,
      variant: "success",
    });
  };

  const handleSaveInvoice = useCallback(async () => {
    if (!selectedPractice || !practiceDetails || !profileData) {
      toast({
        title: "Cannot Save Invoice",
        description: "Please complete your profile and select a practice first",
        variant: "destructive"
      });
      return;
    }
    
    setInvoiceLoading(true);
    
    try {
      let invoiceNum = currentInvoiceNumber;
      if (!invoiceNum) {
        // When saving, pass true to increment the counter
        invoiceNum = await generateInvoiceNumber(true);
      }
      
      // Create properly structured summaryData for Firebase compatibility
      const summaryData = entries
        .filter(entry => entry.date)
        .map(entry => {
          // Calculate total amount for this entry based on rate type
          let entryAmount = 0;
          const entryPatients = [];
  
          if (rateType === 'percentage' && entry.patients) {
            // Process percentage-based entries
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const percentageRate = parseFloat(rateValue) || 0;
              const patientAmount = (count * rate) * (percentageRate / 100);
              entryAmount += patientAmount;
              
              // Add patient data in the EXACT format required
              entryPatients.push({
                count,
                rate,
                description: patient.description || "",
                rateType,
                rateValue: parseFloat(rateValue) || 0
              });
            });
          } else if (rateType === 'per_patient' && entry.patients) {
            // Process per_patient-based entries (similar to percentage but no percentage calculation)
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const patientAmount = count * rate; // No percentage calculation for per_patient
              entryAmount += patientAmount;
              
              // Add patient data in the EXACT format required
              entryPatients.push({
                count,
                rate,
                description: patient.description || "",
                rateType
                // No rateValue for per_patient since each patient has their own rate
              });
            });
          } else if (rateType === 'hourly') {
            // Process hourly-based entries
            const hours = parseFloat(entry.hours) || 0;
            const hourlyRate = parseFloat(rateValue) || 0;
            entryAmount = hours * hourlyRate;
          } else if (rateType === 'fixed') {
            // Process fixed-rate entries
            entryAmount = parseFloat(rateValue) || 0;
          }
          
          // Return the entry in EXACT format matching example structure
          return {
            date: entry.date,
            formattedDate: format(entry.date, "dd MMM yyyy"),
            amount: entryAmount,
            patients: entryPatients,
            rateType,
            rateValue: parseFloat(rateValue) || 0
          };
        });
      
      // Calculate total patients across all entries
      const totalPatients = summaryData.reduce((sum, entry) => {
        if (entry.patients && entry.patients.length > 0) {
          return sum + entry.patients.reduce((pSum, patient) => pSum + (parseInt(patient.count) || 0), 0);
        }
        return sum;
      }, 0);
      
      // Calculate total amount across all entries
      const totalAmount = summaryData.reduce((sum, entry) => sum + (entry.amount || 0), 0);
      
      // Build the complete invoice data object matching Firebase structure EXACTLY
      const invoiceData = {
        invoiceNumber: invoiceNum,
        practiceId: selectedPractice,
        practiceDetails: {
          ...practiceDetails,
          prac_code: selectedPractice,
          prac_name: practiceDetails.prac_name || '',
          address1: practiceDetails.address1 || '',
          address2: practiceDetails.address2 || 'NA',
          address3: practiceDetails.address3 || 'NA',
          address4: practiceDetails.address4 || '',
          address5: practiceDetails.address5 || '',
          postcode: practiceDetails.postcode || '',
          con_name: practiceDetails.con_name || '',
          icb: practiceDetails.icb || '',
          pcon: practiceDetails.pcon || '',
          sequenceid: practiceDetails.sequenceid || 0,
          type: practiceDetails.type || ''
        },
        rateType,
        rateValue: parseFloat(rateValue) || 0,  // Store as number, not string
        summaryData,
        entriesCount: summaryData.length,
        createdAt: new Date(), 
        totalPatients,
        totalAmount,
        userId: user.uid
        // Removed html field as it's not needed
      };
      
      // Add more error logging to catch any issues in saveInvoice
      try {
        await saveInvoice(user.uid, invoiceData);
      } catch (saveError) {
        console.error('Error in saveInvoice function:', saveError); // Keep essential error logs
        console.error('Full error details:', saveError.stack); // Keep essential error logs
        throw saveError; // Re-throw to trigger the outer catch block
      }
      
      setInvoiceSaved(true);
      setCurrentInvoiceNumber(invoiceNum);
      clearMonthlyDraft();
      
      toast({
        title: "Invoice Saved",
        description: `Invoice ${invoiceNum} has been saved successfully`,
        variant: "success"
      });
      
      return {
        success: true,
        invoiceNumber: invoiceNum
      };
    } catch (error) {
      console.error('Error saving invoice:', error); // Keep essential error logs
      toast({
        title: "Save Failed",
        description: "Could not save the invoice",
        variant: "destructive"
      });
      return false;
    } finally {
      setInvoiceLoading(false);
    }
  }, [selectedPractice, practiceDetails, profileData, previewHtml, currentInvoiceNumber, rateType, rateValue, entries, user, generateInvoiceNumber, toast]);

  const handleGeneratePDF = useCallback(async (returnBlob = false) => {
    if (!previewHtml) {
      toast({
        title: "Cannot Generate PDF",
        description: "Please generate a preview first",
        variant: "destructive"
      });
      return;
    }
    
    setPdfLoading(true);
    
    try {
      const fileName = `Invoice_${currentInvoiceNumber || 'Draft'}.pdf`;
      
      // *** START: Add Date Grouping Logic (similar to InvoiceDetails) ***
      const formatEntriesForPDF = (summaryData, rateType, rateValue) => {
        // 1. Group entries by date
        const groupedByDate = summaryData.reduce((acc, item) => {
          let dateKey;
          // Use entry.date which should be a Date object from the form
          if (item.date instanceof Date && !isNaN(item.date)) {
             dateKey = item.date.toISOString().split('T')[0]; // Use YYYY-MM-DD as key
          } else {
             dateKey = 'no-date'; // Or handle invalid/missing dates appropriately
          }

          if (!acc[dateKey]) {
            acc[dateKey] = {
              date: item.date, // Keep the original Date object
              items: []
            };
          }
          acc[dateKey].items.push(item);
          return acc;
        }, {});

        // 2. Process each date group
        const formattedEntries = Object.values(groupedByDate).map(group => {
          const formattedDate = group.date ? group.date.toLocaleDateString() : 'N/A'; // Format date for PDF
          let combinedDescription = '';
          let dateTotalAmount = 0;
          const descriptionParts = [];

          group.items.forEach(item => {
            // Calculate amount and build description part for this specific item
            let itemAmount = 0;
            if (rateType === 'hourly') {
              const hours = parseFloat(item.hours) || 0;
              const hourlyRate = parseFloat(rateValue) || 0;
              itemAmount = hours * hourlyRate;
              dateTotalAmount += itemAmount;
              // Include description if present
              const desc = item.description || '';
              descriptionParts.push(`${hours} hours × £${hourlyRate.toFixed(2)}${desc ? ` (${desc})` : ''}`);
            } else if (rateType === 'percentage' && item.patients?.length > 0) {
              item.patients.forEach(patient => {
                const count = parseInt(patient.count) || 0;
                const rate = parseFloat(patient.rate) || 0;
                const percentValue = parseFloat(rateValue) || 0;
                const patientAmount = (count * rate) * (percentValue / 100);
                itemAmount += patientAmount; // Add to item total
                const desc = patient.description || '';
                descriptionParts.push(`${count} × £${rate.toFixed(2)} @ ${percentValue}%${desc ? ` (${desc})` : ''}`);
              });
              dateTotalAmount += itemAmount; // Add item total to date total
            } else if (rateType === 'fixed') {
              itemAmount = parseFloat(rateValue) || 0;
              dateTotalAmount += itemAmount;
              // Include description if present
              const desc = item.description || '';
              descriptionParts.push(`Fixed rate: £${itemAmount.toFixed(2)}${desc ? ` (${desc})` : ''}`);
            } else {
              // Fallback for unexpected item structure
              const amount = parseFloat(item.amount || 0); // Assuming amount might exist
              itemAmount = amount;
              dateTotalAmount += itemAmount;
              descriptionParts.push(item.description || 'Invoice item');
            }
          });

          combinedDescription = descriptionParts.join(' | ');

          return {
            date: group.date, // Keep original date object if needed, or just formattedDate
            formattedDate: formattedDate,
            description: combinedDescription,
            amount: dateTotalAmount,
          };
        });

        // Sort by date
        formattedEntries.sort((a, b) => (a.date && b.date ? a.date.getTime() - b.date.getTime() : 0));

        return formattedEntries;
      };

      // Use the grouping function with the current entries state
      const groupedPdfEntries = formatEntriesForPDF(entries, rateType, rateValue);
      // *** END: Add Date Grouping Logic ***


      // *** REMOVE THIS INCORRECT MAPPING ***
      /*
      // Process entries to include all calculated fields required by the PDF generator
      const processedEntries = entries.map(entry => {
         // ... old incorrect logic ...
      });
      */

      // Generate PDF with the properly grouped data
      const pdfResult = await generateInvoicePDF({
        fileName,
        practice: practiceDetails,
        entries: groupedPdfEntries, // *** Pass the grouped entries ***
        total: (total?.amount || 0).toFixed(2),
        invoiceNumber: currentInvoiceNumber || 'Draft',
        profile: profileData,
        author: profileData?.name || 'Dental Hygienist',
        subject: `Invoice ${currentInvoiceNumber || 'Draft'}`,
        returnBlob // Pass the flag
      });
      if (returnBlob) return pdfResult;
      toast({
        title: "PDF Generated",
        description: "Your invoice PDF has been downloaded",
        variant: "success"
      });
    } catch (error) {
      console.error('Error generating PDF:', error); // Keep essential error logs
      console.error('Error details:', error.stack); // Keep essential error logs
      toast({
        title: "PDF Generation Failed",
        description: "Could not generate PDF",
        variant: "destructive"
      });
    } finally {
      setPdfLoading(false);
    }
  }, [previewHtml, currentInvoiceNumber, profileData, practiceDetails, entries, total, toast, rateType, rateValue]); // Added rateType, rateValue, entries to dependencies

  const handleSendInvoice = useCallback(() => {
    if (!invoiceSaved || !currentInvoiceNumber) {
      toast({
        title: "Cannot Send Invoice",
        description: "Please save the invoice first",
        variant: "destructive"
      });
      return;
    }
    
    setEmailLoading(true);
    
    try {
      const subject = encodeURIComponent(`Invoice ${currentInvoiceNumber} from ${profileData?.name || 'Dental Hygienist'}`);
      const body = encodeURIComponent(`Please find attached invoice ${currentInvoiceNumber} for dental hygienist services.\n\nThank you,\n${profileData?.name || ''}`);
      
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
      
      toast({
        title: "Email Client Opened",
        description: "Please attach the generated PDF to your email",
        variant: "success"
      });
    } catch (error) {
      console.error('Error sending invoice:', error); // Keep essential error logs
      toast({
        title: "Email Failed",
        description: "Could not open email client",
        variant: "destructive"
      });
    } finally {
      setEmailLoading(false);
    }
  }, [invoiceSaved, currentInvoiceNumber, profileData, toast]);

  const handlePracticeChange = useCallback((pracCode) => {
    setSelectedPractice(pracCode);
    
    const practice = selectedPractices.find(p => 
      (typeof p === 'object' ? p.prac_code : p) === pracCode
    );
    
    // selectedPractices can still contain raw code strings (legacy Firestore
    // format) instead of enhanced objects — only trust a fully-resolved object
    // here, otherwise a blank practice name silently gets saved on the invoice.
    if (practice && typeof practice === 'object' && practice.prac_name) {
      setPracticeDetails(practice);
    } else {
      getAndCachePractice(pracCode)
        .then(fetchedPractice => {
          if (fetchedPractice) {
            setPracticeDetails(fetchedPractice);
          } else {
            setPracticeDetails(null);
          }
        })
        .catch(error => {
          console.error('Error fetching practice details:', error);
          setPracticeDetails(null);
        });
    }
  }, [selectedPractices, getAndCachePractice]);

  const handlePracticeSelection = useCallback(async (updatedPractices, newlyAddedPracticeCode = null) => {
    if (!user) return;
    
    if (newlyAddedPracticeCode) {
      console.log('Newly added practice to auto-select:', newlyAddedPracticeCode);
    }
    
    try {
      setSelectedPractices(updatedPractices);
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        selectedPractices: updatedPractices.map(p => p.prac_code || p),
        lastUpdated: new Date()
      });
      
      setShowPracticeSettings(false);
      
      if (updatedPractices.length > 0) {
        // AUTO-SELECT LOGIC:
        // 1. If a new practice was just added, auto-select it (best UX)
        // 2. Otherwise, select the first practice in the list
        const practiceToSelect = newlyAddedPracticeCode || updatedPractices[0].prac_code;
        const selectedPracticeDetails = updatedPractices.find(p => p.prac_code === practiceToSelect);
        
        console.log(newlyAddedPracticeCode 
          ? `🎯 Auto-selecting newly added practice: ${practiceToSelect}`
          : `Selecting first practice: ${practiceToSelect}`
        );
        
        handlePracticeChange(practiceToSelect);
        
        toast({
          title: newlyAddedPracticeCode ? "Practice Added" : "Practice Selection Updated",
          description: `Selected practice: ${selectedPracticeDetails?.prac_name || 'Unknown'}`,
          variant: "success"
        });
      }
    } catch (error) {
      console.error('Error saving practice selection:', error);
      toast({
        title: "Error",
        description: "Failed to save practice selection",
        variant: "destructive"
      });
    }
  }, [user, toast, handlePracticeChange]);
  // Update isPro calculation to check nested subscription object and include trialing status
  const isPro = Boolean(
    subscriptionStatus?.subscription?.level === 'pro' &&
    ['active', 'trialing'].includes(subscriptionStatus?.subscription?.status)
  );
  if (process.env.NODE_ENV === 'development') {
    console.log('InvoiceApp - isPro:', isPro, 'Subscription:', subscriptionStatus);
  }

  // More visible PRO badge with higher contrast (similar to MainNav)
  const ProBadge = () => (
    // Style remains the same, high contrast
    <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold text-black bg-green-400 rounded-md border border-green-500 leading-none">
      PRO
    </span>
  );

  // Handler for clicking the disabled Daily Records tab
  const handleDisabledTabClick = (e) => {
    e.preventDefault(); // Prevent the tab from activating
    toast({
      title: "Pro Feature",
      description: "Daily Records is a Pro feature. Please upgrade your plan.",
      variant: "info", // Use info variant
      action: (
        <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.BILLING)}>
          Upgrade
        </Button>
      ),
    });
  };

  // Function to reset the invoice form state
  const handleCreateNewInvoice = useCallback(() => {
    // Suppress the next autosave so the blank reset state is never written to
    // localStorage as a phantom draft.
    pauseMonthlyDraft();
    setEntries([{
      date: null,
      hours: '',
      description: '', // Ensure description is reset
      patients: [{ count: '', rate: '', description: '' }], // Reset patients too
      isExpanded: true
    }]);
    // Keep rateType and rateValue as they might be practice-specific? Or reset? Let's reset for a truly new invoice.
    // setRateType('percentage'); // Optionally reset rate type
    // setRateValue('');         // Optionally reset rate value
    setTotal(null);
    setSummary([]);
    setCurrentInvoiceNumber(null); // Crucial for generating a new number
    setPreviewHtml(null);
    setInvoiceSaved(false);
    setPreviewGenerated(false);
    setPreviewInvoiceNumber(null);
    // Optionally reset selected practice? For now, keep it selected.
    // setSelectedPractice(null);
    // setPracticeDetails(null);

    toast({
      title: "New Invoice Ready",
      description: "Form has been reset. Please enter details for the new invoice.",
      variant: "success",
    });
  }, [pauseMonthlyDraft, setEntries, setTotal, setSummary, setCurrentInvoiceNumber, setPreviewHtml, setInvoiceSaved, setPreviewGenerated, setPreviewInvoiceNumber, toast]);

  // Gating logic for Invoice Actions (Calculations/Preview/Save/Download/Email)
  const actionsEnabled = !!(
    total &&
    summary &&
    Array.isArray(summary) &&
    summary.length > 0 &&
    total.amount > 0
    // Optionally: add more form validity checks here
  );

  const contextValue = useMemo(() => ({
    user,
    selectedPractice,
    selectedPractices, 
    practiceDetails,
    profileData,
    entries,
    rateType,
    rateValue,
    total,
    summary,
    previewHtml,
    invoiceLoading,
    previewLoading,
    pdfLoading,
    emailLoading,
    invoiceSaved,
    currentInvoiceNumber,
    showSummary, // Expose showSummary
    setShowSummary, // Expose setter
    setSelectedPractice: handlePracticeChange,
    setEntries,
    setRateType, 
    setRateValue,
    setTotal,
    setSummary,
    setPreviewHtml,
    setInvoiceLoading,
    setPreviewLoading,
    setPdfLoading,
    setEmailLoading,
    setInvoiceSaved,
    setCurrentInvoiceNumber,
    getPractice,
    formatCurrency,
    handleGeneratePreview,
    generateInvoiceNumber,
    handleSaveInvoice,
    handleGeneratePDF,
    handleSendInvoice,
    calculateTotal,
    activeTab, // Make activeTab available if needed by children
    setActiveTab, // Make setActiveTab available if needed
    isPro, // Add isPro to context if needed by children
    subscriptionStatus, // Add subscriptionStatus if needed
    handleCreateNewInvoice, // Add the new function to context if needed by children (like InvoiceActions)
    actionsEnabled, // <-- Add to context
  }), [
    user, selectedPractice, selectedPractices, practiceDetails, profileData, entries, rateType, rateValue, total, summary, previewHtml, invoiceLoading, previewLoading, pdfLoading, emailLoading, invoiceSaved, currentInvoiceNumber, showSummary, setShowSummary, handlePracticeChange, setEntries, setRateType, setRateValue, setTotal, setSummary, setPreviewHtml, setInvoiceLoading, setPreviewLoading, setPdfLoading, setEmailLoading, setInvoiceSaved, setCurrentInvoiceNumber, getPractice, formatCurrency, handleGeneratePreview, generateInvoiceNumber, handleSaveInvoice, handleGeneratePDF, handleSendInvoice, calculateTotal, activeTab, setActiveTab, isPro, subscriptionStatus, handleCreateNewInvoice, actionsEnabled
  ]);

  const calendarStyles = {
    '.rdp': {
      '--rdp-accent-color': 'rgb(124 58 237)',
    },
    '.rdp-month': {
      backgroundColor: '#2A2A3E',
    },
    '.rdp-day_selected:not([disabled])': {
      backgroundColor: 'rgb(124 58 237)',
      color: 'white',
    },
    '.rdp-day_selected:hover:not([disabled])': {
      backgroundColor: 'rgb(109 40 217)',
    },
    '.rdp-day:hover:not([disabled])': {
      backgroundColor: 'rgba(124, 58, 237, 0.2)',
      color: 'white',
    },
    '.rdp-button:hover:not([disabled])': {
      backgroundColor: 'rgba(124, 58, 237, 0.2)',
      color: 'white',
    },
    '.rdp-head_cell': {
      color: '#d1d5db',
      fontWeight: '500',
      fontSize: '0.875rem',
    },
    '.rdp-nav_button': {
      position: 'absolute',
      top: '0.5rem',
      color: '#d1d5db',
    },
    '.rdp-nav_button_previous': {
      left: '0.5rem',
    },
    '.rdp-nav_button_next': {
      right: '0.5rem',
    },
    '.rdp-caption': {
      position: 'relative',
      margin: '0 auto',
      textAlign: 'center',
      color: '#f3f4f6',
      fontSize: '1rem',
      lineHeight: '1.5',
      fontWeight: '500',
      padding: '0 2rem',
    },
    '.rdp-day': {
      color: '#f3f4f6',
    },
    '.rdp-day_outside': {
      color: '#6b7280',
    },
    '.rdp-day_disabled': {
      color: '#4b5563',
    },
  };

  const EnhancedDatePicker = ({ selected, onSelect, entryIndex }) => {
    return (
      <div className="p-2 bg-[#1E1E2D] text-white rounded-lg">
        <SimpleCalendar
          value={selected}
          onChange={(date) => {
            onSelect(date);
            setCalendarOpen(null); // Close calendar after date selection
          }}
          className="bg-[#1E1E2D] text-white border-none"
        />
      </div>
    );
  };

  const handleDateSelect = (entryIndex, date) => {
    const newEntries = [...entries];
    newEntries[entryIndex] = {
      ...newEntries[entryIndex],
      date
    };
    setEntries(newEntries);
    setCalendarOpen(null);
  };

  return (
    <InvoiceContext.Provider value={contextValue}>
      <RequireAuth user={user}>
        <div className="container mx-auto p-4">
          {/* ── Monthly draft restore banner ─────────────────────────────── */}
          {monthlyDraft && (() => {
            // Prefer name stored in draft (new drafts).
            // For old drafts (no selectedPracticeName), look up from the in-memory
            // practices cache once it has been initialised from the JSON file.
            const draftPracticeName = monthlyDraft.selectedPracticeName
              || (monthlyDraft.selectedPractice && practicesInitialized
                ? practiceDataUtil.practiceCache.allPractices.find(p => p.prac_code === monthlyDraft.selectedPractice)?.prac_name
                : null)
              || null;
            return (
              <div className={cn(
                'flex items-center justify-between gap-3 rounded-lg border px-4 py-3 mb-4 text-sm',
                theme === 'light'
                  ? 'bg-amber-50 border-amber-300 text-amber-900'
                  : 'bg-amber-900/20 border-amber-600/40 text-amber-300'
              )}>
                <span>
                  📋 You have an unsaved draft
                  {draftPracticeName ? <> for <strong>{draftPracticeName}</strong></> : ''}.
                  {' '}Restore it?
                </span>
                <div className="flex gap-3 shrink-0 items-center">
                  <button
                    onClick={handleRestoreMonthlyDraft}
                    className="font-semibold underline underline-offset-2 hover:opacity-80"
                  >
                    Restore
                  </button>
                  <span className="opacity-30">|</span>
                  <button
                    onClick={dismissMonthlyDraft}
                    className="opacity-60 hover:opacity-90 underline underline-offset-2"
                    aria-label="Discard draft"
                  >
                    Discard
                  </button>
                </div>
              </div>
            );
          })()}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex items-center justify-between mb-4">
              {/* TabsList: Theme-aware */}
              <TabsList className={cn(
                theme === 'light' ? 'bg-gray-200 border-gray-300' : 'bg-[#2A2A3E] border-purple-500/20'
              )}>
                {/* TabsTrigger: Theme-aware */}
                <TabsTrigger 
                  value="monthly"
                  className={cn(
                    "data-[state=active]:text-white", // Keep active text white
                    theme === 'light' 
                      ? 'data-[state=active]:bg-purple-600 text-gray-700' // Light theme active/inactive
                      : 'data-[state=active]:bg-purple-600 text-purple-300' // Dark theme active/inactive
                  )}
                >
                  Monthly Invoice
                </TabsTrigger>
                {/* Conditionally render or disable Daily Records Tab Trigger */}
                {isPro ? (
                  <TabsTrigger 
                    value="daily"
                    className={cn(
                      "data-[state=active]:text-white", // Keep active text white
                      theme === 'light' 
                        ? 'data-[state=active]:bg-purple-600 text-gray-700' // Light theme active/inactive
                        : 'data-[state=active]:bg-purple-600 text-purple-300' // Dark theme active/inactive
                    )}
                  >
                    Daily Records
                  </TabsTrigger>
                ) : (
                  <TabsTrigger
                    value="daily-disabled" // Use a different value to prevent accidental activation
                    disabled // Disable the trigger
                    // Disabled state styling: Theme-aware
                    className={cn(
                      "cursor-not-allowed relative", 
                      theme === 'light' ? 'text-gray-400' : 'text-gray-500' 
                    )}
                    onClick={handleDisabledTabClick} // Add click handler for toast/navigation
                  >
                    Daily Records
                    <ProBadge />
                  </TabsTrigger>
                )}
              </TabsList>
              {/* HelpIcon should adapt internally or via props */}
              <HelpIcon sectionKey={activeTab === 'monthly' ? 'InvoiceAppMonthly' : (isPro ? 'InvoiceAppDaily' : 'InvoiceAppMonthly')} />
            </div>
            
            <TabsContent value="monthly">
              <div className="space-y-4">
                {/* Practice Selector - Assumed theme-aware internally */}
                <PracticeSelector
                  onSelectPractice={handlePracticeChange}
                  onOpenSettings={() => setShowPracticeSettings(true)}
                />
                
                {/* Show form only when a practice is selected */}
                {selectedPractice ? (
                  <>
                    {/* MonthlyInvoiceForm - Already updated */}
                    <MonthlyInvoiceForm />

                    {/* InvoiceSummary - Only show if explicitly requested */}
                    {showSummary && <InvoiceSummary />}

                    {/* InvoicePreview - Assumed theme-aware internally */}
                    {/* InvoicePreview is now only shown in the modal, not inline */}

                    {/* InvoiceActions - Assumed theme-aware internally */}
                    <InvoiceActions />

                    {/* Create New Invoice Button: Theme-aware */}
                    <div className="flex justify-center mt-6">
                      <Button
                        variant="outline"
                        onClick={handleCreateNewInvoice}
                        className={cn(
                          theme === 'light' 
                            ? 'text-purple-700 border-purple-300 hover:bg-purple-100' 
                            : 'text-purple-300 border-purple-500/50 hover:bg-purple-900/30'
                        )}
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Create New Invoice (Reset Form)
                      </Button>
                    </div>
                  </>
                ) : (
                  // Fallback Card: Theme-aware
                  <Card className={cn(
                    "shadow-lg",
                    theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
                  )}>
                    <CardContent className="p-4 text-center">
                      {/* Text: Theme-aware */}
                      <p className={cn(theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                        {selectedPractices.length > 0 
                          ? "Please select a practice to continue" 
                          : "Please add practices using the 'Manage' button above"}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Conditionally render Daily Records Tab Content only if Pro */}
            {isPro && (
              <TabsContent value="daily"> {/* Add Daily Records content */}
                {/* DailyRecordLog - Needs theme update if not already done */}
                <DailyRecordLog 
                  user={user}
                  selectedPractices={selectedPractices}
                  profileData={profileData}
                />
              </TabsContent>
            )}
            {/* No content needed for the disabled tab state */}
          </Tabs>
          
          {/* Practice Settings Dialog - Content should be theme-aware */}
          <Dialog open={showPracticeSettings} onOpenChange={setShowPracticeSettings}>
            {/* DialogContent: Theme-aware */}
            <DialogContent className={cn(
              "max-w-4xl max-h-[80vh] overflow-auto", // Keep sizing
              theme === 'light' ? 'bg-white text-black' : 'bg-[#2A2A3E] border-purple-500/20 text-white'
            )}>
              {/* DialogHeader: Theme-aware */}
              <DialogHeader>
                <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-purple-300')}>Manage Practices</DialogTitle>
              </DialogHeader>
              {/* PracticeSettings component should handle its internal theme */}
              <PracticeSettings 
                initialSelectedPractices={selectedPractices}
                onSave={handlePracticeSelection}
                onClose={() => setShowPracticeSettings(false)}
                user={user}
              />
            </DialogContent>
          </Dialog>

        </div>
      </RequireAuth>
    </InvoiceContext.Provider>
  );
};

const InvoiceAppWithContext = ({ user, initialSelectedPractices = [], subscriptionStatus }) => {
  return (
    <PracticeDataProvider>
      <RequireAuth>
        <InvoiceApp user={user} initialSelectedPractices={initialSelectedPractices} subscriptionStatus={subscriptionStatus} />
      </RequireAuth>
    </PracticeDataProvider>
  );
};

export default InvoiceAppWithContext;
